import React, { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import noop from 'lodash/noop';
import styled from '@emotion/styled';
import { themeGet } from 'styled-system';
import { Flex, Box, Button, NakedButton, Checkbox, Text, Link, Label, Heading } from 'roo-ui/components';
import { StepHeader, EDITING } from 'components/StepWizard';
import { getIsCreditCardValid, getRequiresCreditCardPayment } from 'store/checkout/checkoutSelectors';
import { createBooking } from 'store/booking/bookingActions';
import { mediaQuery } from 'lib/styledSystem';
import { getStayDates, getQuote } from 'store/quote/quoteSelectors';
import { getIsCreating } from 'store/booking/bookingSelectors';
import theme from 'lib/theme';
import BookingProgressDialog from './BookingProgressDialog';
import PriceDetails from './PriceDetails';
import { useDataLayer } from 'hooks/useDataLayer';
import Markup from 'components/Markup';
import { useModal } from 'lib/hooks';
import Modal from 'components/Modal';
import CreditCard from './CreditCard';
import CreditCardIcons from './CreditCardIcons';
import BookingSummary from './BookingSummary';

const AcceptTermsText = styled(Text)`
  color: ${themeGet('colors.greys.charcoal')};
  display: inline-block;
  font-size: ${themeGet('fontSizes.base')};
  font-weight: ${themeGet('fontWeights.normal')};
  height: inherit;
  width: auto;
  margin: 0 ${themeGet('space.1')} 0 0;

  ${mediaQuery.mobileOnlyEscapeHatch} {
    font-size: ${themeGet('fontSizes.sm')};
  }
`;

const TermsAndConditionsLink = styled(Link)`
  color: ${themeGet('colors.greys.charcoal')};
  font-size: ${themeGet('fontSizes.base')};
  font-weight: ${themeGet('fontWeights.normal')};
  text-decoration: underline;
  flex-direction: column;

  margin: 2px ${themeGet('space.1')} 0 0;

  ${mediaQuery.mobileOnlyEscapeHatch} {
    font-size: ${themeGet('fontSizes.sm')};
  }
`;

const TermsAndConditionsLabel = styled(Label)`
  line-height: 1.3;
`;

const ConfirmAndPay = ({ step, ...rest }) => {
  const { containerRef, hasState, edit, stepNumber } = step;
  const { openModal, modalProps } = useModal();
  const dispatch = useDispatch();
  const isCreating = useSelector(getIsCreating);
  const { property, offer } = useSelector(getQuote) || {};
  const [isTermsAccepted, setTermsAccepted] = useState(false);
  const { emitInteractionEvent } = useDataLayer();
  const isCreditCardValid = useSelector(getIsCreditCardValid);
  const requiresCreditCardPayment = useSelector(getRequiresCreditCardPayment);
  const { checkIn, checkOut } = useSelector(getStayDates);

  const toggleTermsAccepted = useCallback(() => {
    const newTermsAccepted = !isTermsAccepted;
    setTermsAccepted(newTermsAccepted);
    emitInteractionEvent({ type: 'Policy and Terms Links', value: `Agree Opted ${newTermsAccepted ? 'In' : 'Out'}` });
  }, [isTermsAccepted, emitInteractionEvent]);

  const handlePrivacyPolicyClicked = useCallback(() => {
    emitInteractionEvent({ type: 'Policy and Terms Links', value: 'Privacy Policy Selected' });
  }, [emitInteractionEvent]);

  const handleTermsConditionsClicked = useCallback(() => {
    emitInteractionEvent({ type: 'Policy and Terms Links', value: 'Terms And Conditions Link Selected' });
  }, [emitInteractionEvent]);

  const openCancellationPolicy = useCallback(() => {
    openModal();
    emitInteractionEvent({ type: 'Policy and Terms Links', value: 'Hotel Cancellation Policy Selected' });
  }, [emitInteractionEvent, openModal]);

  const submitBooking = useCallback(() => {
    if (isCreditCardValid) {
      dispatch(createBooking());
      emitInteractionEvent({ type: 'Pay Now Button', value: 'Button Selected' });
    }
  }, [isCreditCardValid, dispatch, emitInteractionEvent]);

  const isSubmitDisabled = isCreating || !isTermsAccepted || !isCreditCardValid;
  const stepSubTitle = requiresCreditCardPayment ? <CreditCardIcons /> : null;

  return (
    <Fragment>
      <Box bg="white" borderRadius="default" boxShadow="hard" ref={containerRef} {...rest}>
        <StepHeader title="Confirm & pay" subTitle={stepSubTitle} edit={edit} hasState={hasState} stepNumber={stepNumber} />

        {hasState(EDITING) && (
          <Fragment>
            {requiresCreditCardPayment && (
              <Box p={[4, 8]}>
                <CreditCard />
              </Box>
            )}
            <Box p={[4, 8]} borderTop={1} borderColor="greys.alto">
              <Heading.h3 fontSize="md" fontWeight="normal">
                Please confirm your booking details.
              </Heading.h3>
              <BookingSummary />
              <PriceDetails property={property} offer={offer} checkIn={checkIn} checkOut={checkOut} />

              <Flex mt={8}>
                <Box>
                  <TermsAndConditionsLabel htmlfor="acceptTerms">
                    <Checkbox
                      aria-label="I agree to terms and conditions, hotel cancellation policy and Privacy Policy"
                      data-testid="accept-terms"
                      id="acceptTerms"
                      name="acceptTerms"
                      checked={isTermsAccepted}
                      onChange={toggleTermsAccepted}
                    />
                    <Text hidden>I agree to terms and conditions, hotel cancellation policy and Privacy Policy</Text>
                  </TermsAndConditionsLabel>
                </Box>
                <Flex flexWrap="wrap">
                  <AcceptTermsText>I agree to</AcceptTermsText>
                  <TermsAndConditionsLink
                    href="/hotels/terms-and-conditions"
                    rel="noopener"
                    target="_blank"
                    onClick={handleTermsConditionsClicked}
                    aria-label="View terms and conditions in a new tab"
                  >
                    terms & conditions,
                  </TermsAndConditionsLink>
                  <TermsAndConditionsLink
                    as={NakedButton}
                    onClick={openCancellationPolicy}
                    hoverColor="inherit"
                    data-testid="cancellation-policy-button"
                    aria-label="View hotel cancellation policy"
                  >
                    hotel cancellation policy
                  </TermsAndConditionsLink>
                  <Text fontSize={['sm', 'base']} mr={1}>
                    and
                  </Text>
                  <TermsAndConditionsLink
                    onClick={handlePrivacyPolicyClicked}
                    href="/au/en/support/privacy-and-security.html"
                    rel="noopener"
                    target="_blank"
                    aria-label="View privacy policy in a new tab"
                  >
                    Privacy Policy.
                  </TermsAndConditionsLink>
                </Flex>
              </Flex>
            </Box>
            <Box p={[4, 8]} borderTop={`1px solid ${theme.colors.greys.alto}`}>
              <Button
                onClick={submitBooking}
                disabled={isSubmitDisabled}
                variant="primary"
                data-testid="payment-button"
                width={['100%', 'auto']}
              >
                Pay Now
              </Button>
              <Box mt={3}>
                <Text color="greys.steel">Your payment will be processed in Australia</Text>
              </Box>
            </Box>
          </Fragment>
        )}
      </Box>
      {isCreating && <BookingProgressDialog />}
      <Modal {...modalProps} title="Cancellation policy">
        <Text display="block" mb="5" data-testid="cancellation-policy-description">
          <Heading.h4 fontSize="sm">Cancellation policy</Heading.h4>
          <Markup content={offer.cancellationPolicy.description} />
        </Text>
      </Modal>
    </Fragment>
  );
};

ConfirmAndPay.propTypes = {
  step: PropTypes.shape({
    data: PropTypes.object,
    containerRef: PropTypes.shape({ current: PropTypes.any }),
    hasState: PropTypes.func,
    edit: PropTypes.func,
    stepNumber: PropTypes.number,
  }),
};

ConfirmAndPay.defaultProps = {
  step: {
    data: {},
    containerRef: { current: null },
    hasState: noop,
    edit: noop,
  },
};

export default ConfirmAndPay;
